import React, { useState, useEffect } from 'react';
import { Smartphone, X, Download } from 'lucide-react';
import { isStandalonePWA, detectMobileOS } from '../hooks/usePushNotifications';

const STORAGE_KEY = 'atlas_pwa_banner_dismissed_v1';

/**
 * Mobile-only banner prompting the user to install ATLAS as a PWA.
 * - Hidden on desktop.
 * - Hidden if already in standalone mode.
 * - Hidden if previously dismissed (localStorage).
 * - iOS: instructions only (no prompt API available).
 * - Android: shows native install prompt when available (beforeinstallprompt).
 */
const PWAInstallBanner = () => {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [os] = useState(detectMobileOS());
  const [standalone] = useState(isStandalonePWA());

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* noop */ }
    setDeferredPrompt(null);
    dismiss();
  };

  if (dismissed || standalone || os === 'other') return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="relative flex items-start gap-3 p-4 rounded-xl border border-atlasCyan/25 bg-atlasCyan/5 mb-4"
    >
      <Smartphone className="w-5 h-5 text-atlasCyan flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 pr-6">
        {os === 'ios' ? (
          <p className="text-sm text-white leading-relaxed">
            Installe ATLAS sur ton écran d'accueil pour recevoir les notifications.
            <span className="text-atlasTextSecondary"> Bouton Partager → Sur l'écran d'accueil.</span>
          </p>
        ) : (
          <div>
            <p className="text-sm text-white leading-relaxed mb-2">
              Installe ATLAS sur ton écran d'accueil pour la meilleure expérience.
            </p>
            {deferredPrompt && (
              <button
                data-testid="pwa-install-btn"
                onClick={triggerInstall}
                className="inline-flex items-center gap-1 text-xs font-semibold text-atlasCyan hover:text-white transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Installer ATLAS
              </button>
            )}
          </div>
        )}
      </div>
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        className="absolute top-3 right-3 p-1 text-atlasTextSecondary hover:text-white"
        aria-label="Fermer"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PWAInstallBanner;
